<template>
  <section class="mdp">
    <header class="mdp-top">
      <button type="button" class="mdp-back" @click="$emit('back')">
        <v-icon size="18">mdi-chevron-left</v-icon><span>{{ backLabel }}</span>
      </button>
    </header>

    <div class="mdp-scroll">
      <div class="mdp-head">
        <h2 class="mdp-title">Hotspot</h2>
        <span class="mdp-status is-hotspot">{{ (anomalies.length) }} linked</span>
      </div>
      <div v-if="areaName" class="mdp-meta">[{{ areaName }}]</div>

      <!-- Image -->
      <div class="mdp-image">
        <img v-if="imageSrc" :src="imageSrc" class="mdp-image-img" alt="Hotspot image" @error="useDefaultImage" />
        <div v-else class="mdp-image-ph"><v-progress-circular indeterminate color="#66c7b4" size="28" width="2" /></div>
      </div>

      <!-- Full-image (the equirect for 360) + change image (re-draw the box in street view). -->
      <div v-if="provMeta.loading || provMeta.hasProvenance || canReimage" class="mdp-media">
        <button type="button" class="mdp-media-btn" title="View the full source image"
          :disabled="provMeta.loading || provViewerLoading || !provMeta.hasProvenance"
          @click="openProvenanceImage()">
          <v-progress-circular v-if="provMeta.loading" indeterminate size="14" width="2" color="#66c7b4" />
          <v-icon v-else size="15">mdi-image-outline</v-icon><span>View Full Image</span>
        </button>
        <button v-if="canReimage" type="button" class="mdp-media-btn"
          title="Re-draw this hotspot's box in street view to set its image"
          @click="emit('reimageInStreetView', hotSpot)">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <svg class="mdp-peg" viewBox="0 0 44 68" width="12" height="18" v-html="pegmanSvg"></svg>
          <span>{{ provMeta.hasProvenance ? 'Change image' : 'Add image' }}</span>
        </button>
      </div>

      <!-- Actions -->
      <div class="mdp-actions">
        <button v-if="allowTransform" type="button" class="mdp-act" :disabled="!can(Permissions.HotspotUpdate)"
          @click="$emit('transformHotSpot', hotSpot)">
          <v-icon size="17">mdi-cursor-move</v-icon><span>Move</span>
        </button>
        <v-menu v-if="can(Permissions.HotspotDelete)" location="bottom end" offset="4">
          <template #activator="{ props: menuProps }">
            <button type="button" class="mdp-act mdp-more" v-bind="menuProps" aria-label="More actions">
              <v-icon size="20">mdi-dots-horizontal</v-icon>
            </button>
          </template>
          <v-list class="mdp-menu" density="compact">
            <v-list-item class="danger-item" @click="showDeleteDialog = true">
              <template #prepend><v-icon size="18" color="#f87171">mdi-delete-outline</v-icon></template>
              <v-list-item-title>Delete</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Associated anomalies -->
      <div class="mdp-section">
        <div class="mdp-section-head">
          <span class="mdp-section-title">Associated anomalies</span>
          <span class="tab-count">{{ anomalies.length }}</span>
        </div>

        <div v-if="loading" class="mdp-empty"><v-progress-circular indeterminate color="#66c7b4" size="24" /></div>
        <div v-else-if="anomalies.length" class="mdp-anomalies">
          <MarkerList :markers="anomalies" :buttonEnabled="true" @OnMarkerSelected="(m) => $emit('navigateToMarker', m)" />
        </div>
        <div v-else class="mdp-empty"><p class="empty-title">No anomalies linked to this hotspot.</p></div>
      </div>
    </div>

    <v-dialog v-model="showDeleteDialog" max-width="420">
      <div class="confirm-modal">
        <div class="confirm-icon danger"><v-icon size="26" color="#f87171">mdi-delete-outline</v-icon></div>
        <h2 class="confirm-title">Delete hotspot?</h2>
        <p class="confirm-text">Are you sure you want to delete this hotspot?</p>
        <div class="confirm-actions">
          <button type="button" class="btn-ghost" @click="showDeleteDialog = false">Cancel</button>
          <button type="button" class="btn-danger" @click="confirmDeleteYes">Yes, delete</button>
        </div>
      </div>
    </v-dialog>

    <ProvenanceImageOverlay v-model="provViewerOpen" :title="provViewer.title" :source-url="provViewer.sourceUrl" :source-loading="provViewer.sourceLoading"
      :source-file-name="provViewer.sourceFileName" :bbox="provViewer.bbox" :bbox-label="provViewer.label"
      :reference-id="provViewer.referenceId"
      :is360="provViewer.is360" :raw-box="provViewer.rawBox"
      :equirect-url="provViewer.equirectUrl" :equirect-loading="provViewer.equirectLoading"
      :start-mode="provViewer.startMode" @request-equirect="loadEquirectForViewer" />
  </section>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import MarkerList from './list/MarkerList.vue';
import ProvenanceImageOverlay from '@/components/ProvenanceImageOverlay.vue';
import HotSpotService from '@/services/HotSpotService';
import MarkerProvenanceService from '@/services/MarkerProvenanceService';
import DualloFramesClient from '@/clients/duallo/DualloFramesClient';
import { buildCubeStrip, buildCubeEquirect, cubeFaceLabelOf, cubeBaseOf, faceFovDegOf, fovDegFromHelper } from '@/lib/cubeStitch';
import { useOrganizationStore } from '@/store/organizationStore';
import organizationSasService from '@/services/OrganizationSasService';
import { useDefaultImage } from '@/utils/ImageUtils';
import { PEGMAN_SVG } from '@/lib/pegmanSvg';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';

const props = defineProps({
  hotSpot: { type: Object, required: true },
  areaName: { type: String, default: '' },
  // Move/reposition is a precise-pointer action — desktop only, never on touch.
  allowTransform: { type: Boolean, default: false },
  // Label for the Back button — reflects where this hotspot was opened from (list or zone).
  backLabel: { type: String, default: 'Markers' },
  // Bumped by the parent after a re-capture so the panel re-signs the thumbnail + reloads provenance.
  imageRefresh: { type: Number, default: 0 },
});

const emit = defineEmits(['back', 'transformHotSpot', 'onHotSpotDeleted', 'onHotSpotThumbnailUpdated', 'navigateToMarker', 'reimageInStreetView']);

const { can } = usePermissions();
const organizationStore = useOrganizationStore();
const pegmanSvg = PEGMAN_SVG;

const anomalies = ref([]);
const loading = ref(false);
const imageSrc = ref(null);
const showDeleteDialog = ref(false);

// Re-capture (change image) is a precise-pointer action, so desktop-only (same as Move).
const canReimage = computed(() => can(Permissions.HotspotUpdate) && props.allowTransform);

// Full-image / street-view provenance viewer (same component the marker + findings screens use).
const provViewerOpen = ref(false);
const provViewerLoading = ref(false);
const provMeta = ref({ loading: false, hasProvenance: false, is360: false, record: null });
const provViewer = ref({
  title: '', label: '', sourceUrl: null, sourceFileName: null, bbox: null,
  is360: false, equirectUrl: null, equirectLoading: false, base: null, startMode: 'source',
});

async function loadProvMeta() {
  const h = props.hotSpot;
  provMeta.value = { loading: !!h?.id, hasProvenance: false, is360: false, record: null };
  if (!h?.id) return;
  const hotSpotId = h.id;
  try {
    const match = await MarkerProvenanceService.getForMarker(h.scanId, h);
    if (props.hotSpot?.id !== hotSpotId) return;
    // Exact id match only — getForMarker falls back to nearest-position, which for a hotspot with no
    // provenance of its own would wrongly latch onto a neighbouring marker's source image.
    const rec = (match?.canonical?.markerId === hotSpotId) ? match.canonical : null;
    provMeta.value = {
      loading: false,
      hasProvenance: !!rec,
      is360: !!rec && /_cube_/i.test(rec.sourceImageFileName || ''),
      record: rec,
    };
  } catch {
    if (props.hotSpot?.id === hotSpotId) provMeta.value = { loading: false, hasProvenance: false, is360: false, record: null };
  }
}

async function openProvenanceImage(startMode = 'source') {
  const h = props.hotSpot;
  if (!h?.id || provViewerLoading.value) return;
  provViewerLoading.value = true;
  try {
    let rec = provMeta.value.record;
    if (!rec) {
      const match = await MarkerProvenanceService.getForMarker(h.scanId, h);
      rec = (match?.canonical?.markerId === h.id) ? match.canonical : null;
    }
    if (!rec) return;
    const faceLabel = cubeFaceLabelOf(rec.sourceImageFileName);
    const isLegacyCube = !!faceLabel;
    const is360 = !isLegacyCube && /_cube_/i.test(rec.sourceImageFileName || '');
    const r = rec.bboxRect;
    // Open immediately (shimmer), resolve the image in the background.
    provViewer.value = {
      title: 'Hotspot',
      label: rec.label || 'Hotspot',
      referenceId: h.externalReference || null,
      sourceUrl: null,
      sourceLoading: true,
      sourceFileName: rec.sourceImageFileName,
      bbox: { left: r.left, top: r.top, width: r.width, height: r.height },
      rawBox: false,
      is360,
      equirectUrl: null,
      equirectLoading: false,
      base: is360 ? rec.sourceImageFileName.split(/_cube_/i)[0] : null,
      startMode: (startMode === 'equirect' && is360) ? 'equirect' : 'source',
    };
    provViewerOpen.value = true;
    const hotSpotId = h.id;

    if (isLegacyCube) {
      // Stitch the rig's faces into one panorama (equirect from 6 faces, else a 360° side strip) so the
      // full-size view shows the whole detection in context, not a single face.
      const pano = await buildLegacyPanorama(rec, faceLabel, h.scanId).catch(() => null);
      if (!provViewerOpen.value || props.hotSpot?.id !== hotSpotId) return;
      if (pano) {
        provViewer.value = { ...provViewer.value, sourceUrl: pano.url, bbox: pano.box || provViewer.value.bbox, rawBox: true, sourceFileName: pano.base, sourceLoading: false };
      } else {
        const sourceUrl = await MarkerProvenanceService.getSourceImageUrl(rec, {
          scanId: h.scanId, assetId: h.assetId, orgId: organizationStore.organizationId,
        }).catch(() => null);
        if (provViewerOpen.value && props.hotSpot?.id === hotSpotId) provViewer.value = { ...provViewer.value, sourceUrl, sourceLoading: false };
      }
      return;
    }

    const sourceUrl = await MarkerProvenanceService.getSourceImageUrl(rec, {
      scanId: h.scanId, assetId: h.assetId, orgId: organizationStore.organizationId,
    }).catch(() => null);
    if (provViewerOpen.value && props.hotSpot?.id === hotSpotId) {
      provViewer.value = { ...provViewer.value, sourceUrl, sourceLoading: false };
    }
  } finally {
    provViewerLoading.value = false;
  }
}

// Stitch a legacy capture's faces into a panorama and map the detection's single-face box onto it:
// an on-the-fly equirect when all 6 faces exist, otherwise a 360° side strip. Returns { url, box, base }
// or null (caller then falls back to the single source face).
async function buildLegacyPanorama(rec, faceLabel, scanId) {
  const base = cubeBaseOf(rec.sourceImageFileName);
  const [frames, helpersByStem] = await Promise.all([
    MarkerProvenanceService.getScanFrames(scanId),
    MarkerProvenanceService.getCameraHelpersByStem(scanId).catch(() => ({})),
  ]);
  const faces = (frames || [])
    .filter(f => cubeBaseOf(f.fileName) === base && cubeFaceLabelOf(f.fileName))
    // FOV from the camera-helper intrinsics first (filename-independent), falling back to the _fNN_ stamp.
    .map(f => ({ label: cubeFaceLabelOf(f.fileName), url: f.url, fovDeg: fovDegFromHelper(helpersByStem[f.stem]) ?? faceFovDegOf(f.fileName) }));
  const r = rec.bboxRect;
  const faceBox = { left: r.left, top: r.top, width: r.width, height: r.height };
  const has = (l) => faces.some(f => f.label === l);

  if (has('up') && has('down')) {
    const eq = await buildCubeEquirect(faces).catch(() => null);
    const box = eq && eq.mapBox(faceLabel, faceBox);
    if (eq && box) return { url: eq.dataUrl, box, base };
  }
  const strip = await buildCubeStrip(faces);
  const box = strip && strip.mapBox(faceLabel, faceBox);
  if (strip && box) return { url: strip.dataUrl, box, base };
  return null;
}

async function loadEquirectForViewer() {
  const v = provViewer.value;
  if (!v.base || v.equirectUrl || v.equirectLoading) return;
  v.equirectLoading = true;
  try {
    const orgId = organizationStore.organizationId;
    const paths = await DualloFramesClient.getEquirectFramePaths(orgId, props.hotSpot.assetId, props.hotSpot.scanId);
    const match = (paths || []).find(p => equirectBaseOf(p) === v.base);
    v.equirectUrl = match ? await organizationSasService.getSignedUrl(match.startsWith('/') ? match : `/${match}`) : null;
  } catch {
    v.equirectUrl = null;
  } finally {
    v.equirectLoading = false;
  }
}

function equirectBaseOf(path) {
  const stem = (path || '').split(/[\\/]/).pop().replace(/\.[^.]+$/, '');
  return stem.replace(/[_-](equirect|cube|pano|360)$/i, '');
}

watch(() => props.hotSpot?.id, async () => {
  if (!props.hotSpot) return;
  loading.value = true;
  anomalies.value = [];
  imageSrc.value = await organizationSasService.getSignedUrl(props.hotSpot.thumbnailPath);
  loadProvMeta();
  try {
    anomalies.value = await HotSpotService.getHotSpotAnomalies(props.hotSpot.scanId, props.hotSpot.id) || [];
  } finally {
    loading.value = false;
  }
}, { immediate: true });

// Re-capture changed the image in place (same hotspot id) → re-sign the thumbnail and reload provenance.
watch(() => props.imageRefresh, async () => {
  if (!props.hotSpot?.id) return;
  imageSrc.value = await organizationSasService.getSignedUrl(props.hotSpot.thumbnailPath);
  loadProvMeta();
});

async function confirmDeleteYes() {
  showDeleteDialog.value = false;
  await HotSpotService.deleteHotSpotAsync(props.hotSpot);
  emit('onHotSpotDeleted', props.hotSpot);
}
</script>

<style scoped>
.mdp {
  display: flex; flex-direction: column; height: 100%; min-height: 0; width: 100%;
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04); border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25); color: #fff; overflow: hidden;
}
.mdp-top {
  display: flex; align-items: center; padding: clamp(9px, calc(6px + 0.25vw), 12px) clamp(10px, calc(6px + 0.3vw), 14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.mdp-back {
  display: inline-flex; align-items: center; gap: 3px; background: none; border: none;
  padding: 4px 6px 4px 2px; border-radius: 8px; color: #cfd8dc; font: inherit; font-size: 0.92rem; font-weight: 700; cursor: pointer;
}
.mdp-back:hover { color: #fff; background: rgba(255, 255, 255, 0.05); }

.mdp-scroll {
  flex: 1 1 auto; min-height: 0; overflow-y: auto;
  padding: clamp(12px, calc(8px + 0.3vw), 16px); display: flex; flex-direction: column; gap: 14px;
  scrollbar-width: thin; scrollbar-color: #3a4a52 transparent;
}
.mdp-scroll::-webkit-scrollbar { width: 6px; }
.mdp-scroll::-webkit-scrollbar-track { background: transparent; }
.mdp-scroll::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }
.mdp-scroll::-webkit-scrollbar-thumb:hover { background: var(--duallo-accent); }

.mdp-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.mdp-title { font-size: clamp(1.1rem, calc(0.9rem + 0.4vw), 1.3rem); font-weight: 800; color: #fff; margin: 0; }
.mdp-status { flex: 0 0 auto; padding: 3px 11px; border-radius: 999px; font-size: 0.72rem; font-weight: 700; border: 1px solid transparent; }
.mdp-status.is-hotspot { background: rgba(240, 166, 35, 0.14); color: #f0b968; border-color: rgba(240, 166, 35, 0.4); }
.mdp-meta { margin-top: -8px; font-size: 0.8rem; color: #8aa0aa; }

.mdp-image {
  position: relative; width: 100%; aspect-ratio: 16 / 9; border-radius: 12px; overflow: hidden;
  background: #1c272c; border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex; align-items: center; justify-content: center;
}
.mdp-image-img { width: 100%; height: 100%; object-fit: cover; display: block; }

.mdp-media { display: flex; gap: 8px; }
.mdp-media-btn {
  flex: 1 1 0; display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  padding: 9px 6px; border-radius: 9px; background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12); color: #e6edf0;
  font: inherit; font-size: 0.78rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.mdp-media-btn:hover:not(:disabled) {
  background: rgba(100, 255, 218, 0.1); border-color: rgba(100, 255, 218, 0.3); color: #64ffda;
}
.mdp-media-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.mdp-peg { display: block; flex: 0 0 auto; }

.mdp-actions { display: flex; gap: 8px; }
.mdp-act {
  flex: 1 1 auto; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  height: 40px; border-radius: 10px; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.12);
  color: #e6edf0; font: inherit; font-size: 0.88rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.mdp-act:hover:not(:disabled) { background: rgba(100, 255, 218, 0.08); border-color: rgba(100, 255, 218, 0.4); color: #fff; }
.mdp-act:disabled { opacity: 0.45; cursor: not-allowed; }
.mdp-more { flex: 0 0 auto; width: 44px; }

.mdp-section { display: flex; flex-direction: column; gap: 10px; }
.mdp-section-head { display: flex; align-items: center; gap: 8px; }
.mdp-section-title { font-size: 0.88rem; font-weight: 700; color: #cfd8dc; }
.tab-count { font-size: 0.74rem; color: #667a83; }
.mdp-anomalies { display: flex; flex-direction: column; gap: 10px; }
.mdp-empty { text-align: center; padding: 22px 10px; color: #8aa0aa; }
.empty-title { margin: 0; font-size: 0.9rem; color: #cfd8dc; }

.confirm-modal {
  background: var(--duallo-card-gradient); border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px; box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45); color: #fff; padding: 26px 22px 20px; text-align: center;
}
.confirm-icon { width: 52px; height: 52px; margin: 0 auto 12px; border-radius: 14px; display: flex; align-items: center; justify-content: center; }
.confirm-icon.danger { background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.25); }
.confirm-title { font-size: 1.2rem; font-weight: 700; margin: 0 0 6px; }
.confirm-text { font-size: 0.92rem; color: #b0bec5; margin: 0 0 20px; line-height: 1.5; }
.confirm-actions { display: flex; gap: 10px; justify-content: center; }
.confirm-actions > * { flex: 1 1 0; max-width: 160px; }
.btn-ghost { padding: 10px 18px; border-radius: 10px; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.12); color: #cfd8dc; font: inherit; font-weight: 600; cursor: pointer; }
.btn-danger { padding: 10px 18px; border-radius: 10px; background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.4); color: #f87171; font: inherit; font-weight: 700; cursor: pointer; }

.mdp-menu { background: #1f2c34 !important; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; }
.danger-item :deep(.v-list-item-title) { color: #ff9c9c; }
</style>
